import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Mic, Plus, Trash2, Sparkles, LogOut, Sun, LayoutGrid, FileText, Users, Lightbulb, RefreshCw, Copy, Send, AlertTriangle, Lock, ChevronRight, Award, BarChart3, BookOpen, Film, Activity, Repeat, Plug } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { errText } from "@/lib/api";
import GrabadorPanel from "@/components/GrabadorPanel";
import FichaCuenta from "@/components/panel/FichaCuenta";
import { Card, Avatar, EstadoPunto, BarraAvance, Sparkline, Hero, Etiqueta, Dato, EmptyState, fmtInt, fmtUSD, fmtPct, fmtBrecha, colorBrecha, haceCuanto } from "@/components/panel/PanelUI";

const ROLES_BASE = ["dueño", "gerente", "administración", "contador", "abogado", "ventas", "operaciones", "marketing", "atención al cliente"];

const DRAFT_KEY = "olimpo_cargar_venta_draft";

export default function Admin() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const esAdmin = user?.rol === "admin" || user?.email === "equipo@olimpo.com";

  const [tab, setTab] = useState("hoy");
  const [sel, setSel] = useState(null);

  const [texto, setTexto] = useState("");
  const [datosExtra, setDatosExtra] = useState("");
  const [link, setLink] = useState("");
  const [leyendoLink, setLeyendoLink] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [analizando, setAnalizando] = useState(false);
  const [grabandoReunion, setGrabandoReunion] = useState(false);

  const [form, setForm] = useState({ nombre: "", rubro: "", tamano: "", pais: "Argentina", variante_trato: "", contexto_venta: "", link_grupo_whatsapp: "" });
  const [analizado, setAnalizado] = useState(false);
  const [usuarios, setUsuarios] = useState([{ nombre: "", email: "", rol_cargo: "", contexto: "" }]);
  const [rolOtro, setRolOtro] = useState({});
  const [creando, setCreando] = useState(false);
  const [accesos, setAccesos] = useState([]);

  const [empresas, setEmpresas] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [closer, setCloser] = useState({ nombre: "", email: "", password: "" });
  const [candado, setCandado] = useState(null);
  const [llmSalud, setLlmSalud] = useState(null);
  const [mailSalud, setMailSalud] = useState(null);
  const [vida, setVida] = useState(null);
  const [permisos, setPermisos] = useState(null);
  const [puertasAdmin, setPuertasAdmin] = useState(null);

  const archivoRef = useRef(null);

  // Borrador de "Cargar venta": no se pierde nada aunque se recargue o cambie de pestaña.
  const draftRestaurado = useRef(false);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d.form) setForm((f) => ({ ...f, ...d.form }));
        if (Array.isArray(d.usuarios) && d.usuarios.length) setUsuarios(d.usuarios);
        if (typeof d.texto === "string") setTexto(d.texto);
        if (typeof d.datosExtra === "string") setDatosExtra(d.datosExtra);
        if (d.analizado) setAnalizado(true);
      }
    } catch {}
    draftRestaurado.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!draftRestaurado.current) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, usuarios, texto, datosExtra, analizado }));
    } catch {}
  }, [form, usuarios, texto, datosExtra, analizado]);

  const cargar = async () => {
    try {
      const [e, r] = await Promise.all([api.get("/admin/empresas"), api.get("/admin/panel-resumen")]);
      setEmpresas(e.data.empresas);
      // Mantener la ficha abierta sincronizada con la lista fresca (estado_panel, día, trabada...).
      setSel((prev) => prev ? (e.data.empresas.find((c) => c.id === prev.id) || prev) : prev);
      setResumen(r.data);
      if (esAdmin) { try { const cd = await api.get("/admin/candado-doc"); setCandado(cd.data); } catch {} }
      if (esAdmin) { try { const ls = await api.get("/admin/llm-salud"); setLlmSalud(ls.data); } catch {} }
      if (esAdmin) { try { const ms = await api.get("/admin/mail-salud"); setMailSalud(ms.data); } catch {} }
      if (esAdmin) { try { const cv = await api.get("/admin/chequeo-vida"); setVida(cv.data); } catch {} }
      try { const pm = await api.get("/admin/permisos"); setPermisos(pm.data); } catch {}
      if (esAdmin) { try { const pt = await api.get("/admin/puertas"); setPuertasAdmin(pt.data); } catch {} }
    } catch (err) { toast.error("No pude cargar el panel. Reintentá en un momento."); }
  };

  useEffect(() => {
    if (user && !esAdmin && user.rol !== "closer") { nav("/"); return; }
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const subirArchivo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const audio = /\.(webm|mp3|m4a|wav|mp4|mpeg|mpga)$/i.test(file.name);
      const { data } = await api.post(audio ? "/closer/transcribir-audio" : "/closer/transcribir-archivo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTexto((t) => (t ? t + "\n\n" : "") + data.texto);
      toast.success("Listo, lo sumé a la transcripción.");
    } catch (err) { toast.error(errText(err)); }
    finally { setSubiendo(false); if (archivoRef.current) archivoRef.current.value = ""; }
  };

  const grabarReunion = async (blob, done) => {
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "reunion.webm");
      const { data } = await api.post("/closer/transcribir-audio", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTexto((t) => (t ? t + "\n\n" : "") + data.texto);
      setGrabandoReunion(false);
      toast.success("Transcripción lista.");
    } catch (err) { toast.error(errText(err)); }
    finally { setSubiendo(false); done && done(); }
  };

  const leerLink = async () => {
    const u = link.trim();
    if (!u) { toast.error("Pegá un link primero."); return; }
    setLeyendoLink(true);
    try {
      const { data } = await api.post("/closer/leer-link", { url: u });
      setTexto((t) => (t ? t + "\n\n" : "") + data.texto);
      setLink("");
      toast.success("Listo, traje el texto del link.");
    } catch (err) { toast.error(errText(err)); }
    finally { setLeyendoLink(false); }
  };

  const analizar = async () => {
    if (texto.trim().length < 30) { toast.error("Pegá o subí la reunión primero."); return; }
    setAnalizando(true);
    try {
      const { data } = await api.post("/closer/analizar", { texto, datos_extra: datosExtra });
      setForm({
        nombre: data.resumen.nombre || "", rubro: data.resumen.rubro || "",
        tamano: data.resumen.tamano || "", pais: data.resumen.pais || "Argentina",
        variante_trato: "", link_grupo_whatsapp: "",
        contexto_venta: data.resumen.contexto_venta || "",
      });
      setAnalizado(true);
      toast.success("La IA armó el resumen. Revisalo y corregí lo que haga falta.");
    } catch (err) { toast.error(errText(err)); }
    finally { setAnalizando(false); }
  };

  const setUsuario = (i, campo, val) => setUsuarios((u) => u.map((x, j) => j === i ? { ...x, [campo]: val } : x));
  const addUsuario = () => setUsuarios((u) => [...u, { nombre: "", email: "", rol_cargo: "", contexto: "" }]);
  const delUsuario = (i) => setUsuarios((u) => u.filter((_, j) => j !== i));

  const crearCliente = async () => {
    const faltan = [];
    if (!form.nombre.trim()) faltan.push("el nombre de la empresa");
    if (!form.rubro.trim()) faltan.push("el rubro");
    if (!form.tamano.trim()) faltan.push("el tamaño");
    if (!form.pais.trim()) faltan.push("el país");
    if (!form.contexto_venta.trim()) faltan.push("lo que el asistente tiene que saber");
    const incompletos = usuarios.some((u) => !u.nombre.trim() || !u.email.trim());
    if (usuarios.length === 0 || incompletos) faltan.push("el nombre y el mail de cada persona");
    if (faltan.length > 0) { toast.error(`Antes de crear el acceso, completá: ${faltan.join(", ")}.`); return; }
    const validos = usuarios.filter((u) => u.nombre.trim() && u.email.trim());
    setCreando(true);
    try {
      const { data } = await api.post("/closer/crear-cliente", { ...form, usuarios: validos });
      setAccesos(data.accesos);
      toast.success("Cliente creado. Cuando entre con su mail, ya lo reconocemos.");
      setForm({ nombre: "", rubro: "", tamano: "", pais: "Argentina", variante_trato: "", contexto_venta: "", link_grupo_whatsapp: "" });
      setUsuarios([{ nombre: "", email: "", rol_cargo: "", contexto: "" }]);
      setTexto(""); setDatosExtra(""); setAnalizado(false);
      try { localStorage.removeItem(DRAFT_KEY); } catch {}
      cargar();
    } catch (err) { toast.error(errText(err)); }
    finally { setCreando(false); }
  };

  const formCompleto = form.nombre.trim() && form.rubro.trim() && form.tamano.trim()
    && form.pais.trim() && form.contexto_venta.trim()
    && usuarios.length > 0 && usuarios.every((u) => u.nombre.trim() && u.email.trim());

  const crearCloser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/crear-closer", closer);
      toast.success("Closer creado.");
      setCloser({ nombre: "", email: "", password: "" });
    } catch (err) { toast.error(errText(err)); }
  };

  const copiarLink = (link) => { navigator.clipboard.writeText(link); toast.success("Link copiado."); };
  const linkDe = (a) => `${window.location.origin}/invitacion/${a.token}`;

  const mandarRecordatorio = async (e) => {
    try {
      const { data } = await api.post(`/admin/empresa/${e.id}/recordatorio`);
      if (data.enviados > 0) toast.success(`Recordatorio reenviado a ${data.enviados} persona(s) que todavía no entraron.`);
      else toast("Todos los usuarios ya entraron. No hace falta recordatorio.");
    } catch (err) { toast.error(errText(err)); }
  };

  const copiarLinkEmpresa = async (e) => {
    let token = e.token_invitacion;
    if (!token) {
      try {
        const { data } = await api.post(`/admin/empresa/${e.id}/token`);
        token = data.token;
        setEmpresas((list) => list.map((x) => x.id === e.id ? { ...x, token_invitacion: token } : x));
      } catch (err) { toast.error(errText(err)); return; }
    }
    navigator.clipboard.writeText(`${window.location.origin}/invitacion/${token}`);
    toast.success("Link copiado.");
  };

  const cuentasOrden = [...empresas].sort((a, b) => (b.brecha || 0) - (a.brecha || 0));

  const TABS = [
    { key: "hoy", label: "Hoy", icon: Sun },
    { key: "cuentas", label: "Cuentas", icon: LayoutGrid },
    { key: "cargar", label: "Cargar venta", icon: FileText },
    ...(esAdmin ? [{ key: "closers", label: "Closers", icon: Users }] : []),
    ...(esAdmin ? [{ key: "encuestas", label: "Encuestas", icon: BarChart3 }] : []),
    ...(esAdmin ? [{ key: "catalogo", label: "Catálogo", icon: BookOpen }] : []),
    ...(esAdmin ? [{ key: "conectables", label: "Conectables", icon: Plug }] : []),
    ...(esAdmin ? [{ key: "videos", label: "Videos", icon: Film }] : []),
    ...(esAdmin ? [{ key: "producto", label: "Producto", icon: Activity }] : []),
    { key: "oportunidades", label: "Oportunidades", icon: Lightbulb },
  ];

  return (
    <div className="min-h-screen" style={{ background: "#0A0A0A" }}>
      <div className="max-w-6xl mx-auto px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <img src="/olimpo-helmet.png" alt="Olimpo" style={{ height: 28, width: "auto" }} />
            <div>
              <div className="font-serif" style={{ fontSize: 22, color: "#F5F0E8", lineHeight: 1 }}>Olimpo</div>
              <div className="font-sans" style={{ fontSize: 12, color: "#8A8A8A" }}>{esAdmin ? "Panel interno" : "Panel del closer"} · hola {user?.nombre?.split(" ")[0]}</div>
            </div>
          </div>
          <button onClick={async () => { await logout(); nav("/login"); }} className="font-sans flex items-center gap-2 hover:text-crema transition-colors" style={{ fontSize: 13, color: "#8A8A8A" }} data-testid="admin-salir">
            <LogOut size={15} /> Salir
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-8 relative" style={{ borderBottom: "1px solid #1A1A1A", zIndex: 70, background: "#0A0A0A" }}>
          {TABS.map((t) => {
            const activo = tab === t.key;
            return (
              <button key={t.key} onClick={() => { setTab(t.key); setSel(null); }} data-testid={`panel-tab-${t.key}`}
                className="font-sans flex items-center gap-2 px-4 py-3 transition-colors"
                style={{ fontSize: 14, color: activo ? "#F5F0E8" : "#8A8A8A", borderBottom: activo ? "2px solid #C9A84C" : "2px solid transparent", marginBottom: -1, fontWeight: activo ? 500 : 400 }}>
                <t.icon size={15} strokeWidth={1.7} /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "hoy" && <VistaHoy resumen={resumen} onSel={setSel} />}
        {tab === "cuentas" && <VistaCuentas cuentas={cuentasOrden} onSel={setSel} esAdmin={esAdmin} />}
        {tab === "cargar" && (
          <VistaCargar {...{ texto, setTexto, datosExtra, setDatosExtra, link, setLink, leyendoLink, leerLink, subiendo, analizando, grabandoReunion, setGrabandoReunion, form, setForm, analizado, usuarios, rolOtro, setRolOtro, creando, accesos, archivoRef, subirArchivo, grabarReunion, analizar, setUsuario, addUsuario, delUsuario, crearCliente, formCompleto, copiarLink, linkDe }} />
        )}
        {tab === "closers" && esAdmin && (
          <VistaClosers resumen={resumen} closer={closer} setCloser={setCloser} crearCloser={crearCloser} candado={candado} llmSalud={llmSalud} mailSalud={mailSalud} vida={vida} permisos={permisos} puertasAdmin={puertasAdmin} />
        )}
        {tab === "encuestas" && esAdmin && <VistaEncuestas />}
        {tab === "catalogo" && esAdmin && <VistaCatalogo />}
        {tab === "conectables" && esAdmin && <VistaConectables />}
        {tab === "videos" && esAdmin && <VistaVideos />}
        {tab === "producto" && esAdmin && <VistaProducto />}
        {tab === "oportunidades" && <VistaOportunidades onSel={setSel} cuentas={cuentasOrden} />}
      </div>

      {sel && (
        <FichaCuentaWrap cuenta={sel} onClose={() => setSel(null)} onRecordatorio={mandarRecordatorio} onCopiarLink={copiarLinkEmpresa} onTratoChange={cargar} onCambio={cargar} esAdmin={esAdmin} closers={resumen?.closers || []} onAsignado={cargar} />
      )}
    </div>
  );
}

// ---------- HOY ----------
function VistaHoy({ resumen, onSel }) {
  if (!resumen) return <Cargando />;
  const { alertas = [], oportunidades = [], renovaciones = [], resumen: r = {} } = resumen;
  return (
    <div className="space-y-10">
      {/* Alertas grandes: lo único que domina */}
      <section>
        <div className="font-sans mb-4" style={{ fontSize: 13, color: "#8A8A8A", fontWeight: 500 }}>Lo que tenés que mirar hoy</div>
        {alertas.length === 0 ? (
          <Card className="p-6"><EmptyState icon={Sun} titulo="Hoy no hay nada urgente." sub={`${r.andando_bien || 0} cuentas andando bien.`} /></Card>
        ) : (
          <div className="grid md:grid-cols-3 gap-4" data-testid="hoy-alertas">
            {alertas.map((c) => (
              <button key={c.id} onClick={() => onSel(c)} data-testid={`hoy-alerta-${c.id}`}
                className="text-left rounded-xl p-5 transition-colors" style={{ background: "#0F0F0F", border: "1px solid #1F1F1F" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#141414")} onMouseLeave={(e) => (e.currentTarget.style.background = "#0F0F0F")}>
                <div className="flex items-center gap-3 mb-4">
                  <Avatar nombre={c.nombre} size={40} />
                  <div className="min-w-0">
                    <div className="font-sans truncate" style={{ fontSize: 15, color: "#F5F0E8", fontWeight: 500 }}>{c.nombre}</div>
                    <EstadoPunto estado={c.estado_panel} />
                  </div>
                </div>
                <div className="flex items-end justify-between mb-3">
                  <div><div className="font-serif" style={{ fontSize: 40, color: colorBrecha(c.brecha), lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{fmtBrecha(c.brecha)}</div><Etiqueta>brecha</Etiqueta></div>
                  <div className="text-right"><div className="font-serif" style={{ fontSize: 28, color: "#F5F0E8", fontVariantNumeric: "tabular-nums" }}>{fmtPct(c.avance)}</div><Etiqueta>avanzado</Etiqueta></div>
                </div>
                <BarraAvance avance={c.avance} tiempo={c.tiempo} height={8} />
                <div className="font-sans mt-3" style={{ fontSize: 12, color: "#7A7A7A" }}>{c.dia_txt} · {c.procesos_andando} andando</div>
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-6 mt-4">
          <ChipResumen n={r.total_cuentas} label="cuentas" />
          <ChipResumen n={r.andando_bien} label="al día" color="#0CA30C" />
          <ChipResumen n={r.trabadas} label="trabadas" color="#D03B3B" />
          <ChipResumen n={r.dormidas} label="dormidas" color="#6B6B6B" />
        </div>
      </section>

      <div className="grid md:grid-cols-2 gap-6">
        <section>
          <div className="font-sans mb-4 flex items-center gap-2" style={{ fontSize: 13, color: "#8A8A8A", fontWeight: 500 }}><Lightbulb size={15} strokeWidth={1.7} /> Oportunidades</div>
          <Card className="p-2">
            {oportunidades.length === 0 ? (
              <EmptyState icon={Lightbulb} titulo="Sin oportunidades hoy." sub="Cuando una cuenta tenga algo para sumar, aparece acá." />
            ) : oportunidades.map((c) => (
              <FilaMini key={c.id} cuenta={c} onSel={onSel} derecha={`${fmtInt(c.focos_en_cola)} en la cola`} />
            ))}
          </Card>
        </section>
        <section>
          <div className="font-sans mb-4 flex items-center gap-2" style={{ fontSize: 13, color: "#8A8A8A", fontWeight: 500 }}><RefreshCw size={15} strokeWidth={1.7} /> Renovaciones esta semana</div>
          <Card className="p-2">
            {renovaciones.length === 0 ? (
              <EmptyState icon={RefreshCw} titulo="Sin renovaciones esta semana." sub="El ciclo mensual de cada cuenta se muestra acá cuando se acerca." />
            ) : renovaciones.map((c) => (
              <FilaMini key={c.id} cuenta={c} onSel={onSel} derecha={c.dias_al_ciclo === 0 ? "hoy" : `en ${c.dias_al_ciclo} días`} />
            ))}
          </Card>
        </section>
      </div>
    </div>
  );
}

function ChipResumen({ n, label, color = "#8A8A8A" }) {
  return (
    <div className="flex items-center gap-2">
      <span className="rounded-full" style={{ width: 7, height: 7, background: color }} />
      <Dato className="text-base" color="#E8E8E8">{fmtInt(n || 0)}</Dato>
      <span className="font-sans" style={{ fontSize: 12, color: "#8A8A8A" }}>{label}</span>
    </div>
  );
}

function FilaMini({ cuenta, onSel, derecha }) {
  return (
    <button onClick={() => onSel(cuenta)} className="w-full flex items-center gap-3 px-3 rounded-lg transition-colors" style={{ minHeight: 52 }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "#141414")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")} data-testid={`fila-mini-${cuenta.id}`}>
      <Avatar nombre={cuenta.nombre} size={30} />
      <div className="flex-1 min-w-0 text-left">
        <div className="font-sans truncate" style={{ fontSize: 14, color: "#E8E8E8" }}>{cuenta.nombre}</div>
        <EstadoPunto estado={cuenta.estado_panel} />
      </div>
      <span className="font-sans" style={{ fontSize: 12, color: "#8A8A8A" }}>{derecha}</span>
      <ChevronRight size={15} style={{ color: "#4A4A4A" }} />
    </button>
  );
}

// ---------- CUENTAS ----------
function VistaCuentas({ cuentas, onSel, esAdmin }) {
  const [filtro, setFiltro] = React.useState("todas");
  const [closerF, setCloserF] = React.useState("");
  const closers = [...new Set(cuentas.map((c) => c.cargada_por).filter(Boolean))];

  let lista = cuentas;
  if (filtro === "rojas") lista = lista.filter((c) => c.estado_panel?.key === "critical" || c.estado_panel?.key === "asleep");
  else if (filtro === "amarillas") lista = lista.filter((c) => c.estado_panel?.key === "attention");
  else if (filtro === "sin_entrar") lista = lista.filter((c) => c.sin_entrar);
  else if (filtro === "trabadas") lista = lista.filter((c) => c.trabada);
  else if (filtro === "riesgo") lista = lista.filter((c) => c.promesa_riesgo);
  else if (filtro === "sin_closer") lista = lista.filter((c) => !c.cargada_por);
  if (closerF) lista = lista.filter((c) => c.cargada_por === closerF);

  const FILTROS = [
    { k: "todas", label: "Todas" }, { k: "rojas", label: "Rojas" }, { k: "amarillas", label: "Amarillas" },
    { k: "sin_entrar", label: "Sin entrar nunca" }, { k: "trabadas", label: "Trabadas" }, { k: "riesgo", label: "Promesa en riesgo" },
    ...(esAdmin ? [{ k: "sin_closer", label: "Sin closer asignado" }] : []),
  ];

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {FILTROS.map((f) => (
          <button key={f.k} onClick={() => setFiltro(f.k)} data-testid={`filtro-${f.k}`}
            className="font-sans px-3 py-1.5 rounded-full transition-colors"
            style={{ fontSize: 13, background: filtro === f.k ? "#C9A84C" : "transparent", color: filtro === f.k ? "#0A0A0A" : "#B0B0B0", border: `1px solid ${filtro === f.k ? "#C9A84C" : "#262626"}`, fontWeight: filtro === f.k ? 600 : 400 }}>
            {f.label}
          </button>
        ))}
        {closers.length > 0 && (
          <select value={closerF} onChange={(e) => setCloserF(e.target.value)} data-testid="filtro-closer"
            className="font-sans px-3 py-1.5 rounded-full" style={{ fontSize: 13, background: "#0F0F0F", color: "#B0B0B0", border: "1px solid #262626" }}>
            <option value="">Por closer</option>
            {closers.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
      </div>

      {lista.length === 0 ? <Card className="p-6"><EmptyState icon={LayoutGrid} titulo="No hay cuentas con ese filtro." /></Card> : (
        <Card>
          <div className="hidden md:flex items-center px-5 py-3 font-sans" style={{ fontSize: 12, color: "#6B6B6B" }}>
            <div style={{ width: 230 }}>Empresa</div>
            <div style={{ width: 70 }} className="text-center">Día</div>
            <div style={{ width: 80 }} className="text-right">Avance</div>
            <div style={{ width: 80 }} className="text-right">Brecha</div>
            <div style={{ width: 80 }} className="text-center">Andando</div>
            <div style={{ width: 120 }} className="text-right">Último ingreso</div>
            <div className="flex-1 pl-6">Estado</div>
          </div>
          {lista.map((c) => (
            <button key={c.id} onClick={() => onSel(c)} data-testid={`cuenta-fila-${c.id}`}
              className="w-full flex flex-col md:flex-row md:items-center px-5 transition-colors text-left"
              style={{ minHeight: 56, borderTop: "1px solid #1A1A1A", paddingTop: 10, paddingBottom: 10 }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#141414")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
              <div className="flex items-center gap-3" style={{ width: 230 }}>
                <Avatar nombre={c.nombre} size={34} />
                <div className="font-sans truncate" style={{ fontSize: 14, color: "#F5F0E8", fontWeight: 500 }}>{c.nombre}</div>
                {c.necesita_atencion && (
                  <span data-testid={`cuenta-atencion-${c.id}`} title={(c.atencion_motivos || []).join(" · ")}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md shrink-0"
                    style={{ background: "#2A1010", border: "1px solid #5A2020", color: "#EC8A8A", fontSize: 10, fontWeight: 600 }}>
                    <AlertTriangle size={10} /> atención
                  </span>
                )}
              </div>
              <div style={{ width: 70 }} className="text-center"><Dato className="text-sm" color="#B0B0B0">{c.dia_corto ?? c.dia_txt}</Dato></div>
              <div style={{ width: 80 }} className="text-right"><Dato className="text-sm">{fmtPct(c.avance)}</Dato></div>
              <div style={{ width: 80 }} className="text-right font-mono tabular-nums"><span style={{ color: colorBrecha(c.brecha), fontSize: 14 }}>{fmtBrecha(c.brecha)}</span></div>
              <div style={{ width: 80 }} className="text-center"><Dato className="text-sm">{fmtInt(c.procesos_andando)}</Dato></div>
              <div className="text-right font-sans" style={{ width: 120, fontSize: 12, color: "#8A8A8A" }}>{haceCuanto(c.ultima_actividad)}</div>
              <div className="flex-1 md:pl-6 mt-2 md:mt-0"><EstadoPunto estado={c.estado_panel} /></div>
            </button>
          ))}
        </Card>
      )}
    </div>
  );
}

// ---------- CARGAR VENTA (closer tool) ----------
function VistaCargar(p) {
  const inputCls = "w-full rounded-lg font-sans px-3 py-2.5";
  const inputStyle = { background: "#0F0F0F", border: "1px solid #262626", color: "#E8E8E8", fontSize: 14 };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* PASO 1 */}
      <Card className="p-6">
        <div className="font-sans mb-1" style={{ fontSize: 15, color: "#C9A84C", fontWeight: 500 }}>Paso 1 · La reunión de venta</div>
        <p className="font-sans mb-4" style={{ fontSize: 13, color: "#8A8A8A" }}>Pegá la transcripción, pegá el link de Google Docs, o subí el archivo o el audio. Después la IA saca lo que sirve.</p>
        <textarea className={inputCls + " mb-3"} style={{ ...inputStyle, minHeight: 200, resize: "vertical" }} placeholder="Pegá acá la transcripción de la reunión…" value={p.texto} onChange={(e) => p.setTexto(e.target.value)} data-testid="closer-texto" />
        <div className="flex gap-2 mb-3">
          <input className={inputCls} style={inputStyle} placeholder="Pegá un link de Google Docs (público) y lo traigo…" value={p.link} onChange={(e) => p.setLink(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); p.leerLink(); } }} data-testid="closer-link" />
          <BtnGhost onClick={p.leerLink} disabled={p.leyendoLink} testid="closer-traer-link"><FileText size={14} /> {p.leyendoLink ? "Trayendo…" : "Traer"}</BtnGhost>
        </div>
        <div className="flex flex-wrap gap-3 mb-3">
          <BtnGhost onClick={() => p.archivoRef.current?.click()} disabled={p.subiendo} testid="closer-subir-archivo"><Upload size={14} /> {p.subiendo ? "Subiendo…" : "Subir archivo"}</BtnGhost>
          <input ref={p.archivoRef} type="file" accept=".txt,.md,.pdf,.docx,.mp3,.m4a,.wav,.webm,.mp4" className="hidden" onChange={p.subirArchivo} />
          <BtnGhost onClick={() => p.setGrabandoReunion((v) => !v)} testid="closer-grabar"><Mic size={14} /> Audio de la reunión</BtnGhost>
        </div>
        {p.grabandoReunion && (
          <div className="rounded-lg p-4 mb-3" style={{ border: "1px solid #262626" }}>
            <GrabadorPanel variant="chat" onEnviar={p.grabarReunion} enviando={p.subiendo} onCancelar={() => p.setGrabandoReunion(false)} />
          </div>
        )}
        <textarea className={inputCls + " mb-4"} style={{ ...inputStyle, minHeight: 80, resize: "vertical" }} placeholder="Notas extra tuyas (qué compraron, qué prometimos, quién decide)…" value={p.datosExtra} onChange={(e) => p.setDatosExtra(e.target.value)} data-testid="closer-notas" />
        <BtnGold onClick={p.analizar} disabled={p.analizando} full testid="closer-analizar"><Sparkles size={15} /> {p.analizando ? "Leyendo la reunión…" : "Analizar con IA"}</BtnGold>
      </Card>

      {/* PASO 2 */}
      <Card className="p-6">
        <div className="font-sans mb-1" style={{ fontSize: 15, color: "#C9A84C", fontWeight: 500 }}>Paso 2 · Revisá y creá el cliente</div>
        <p className="font-sans mb-4" style={{ fontSize: 13, color: "#8A8A8A" }}>La IA propone, vos confirmás. Esto es lo que el asistente va a saber desde el primer mensaje.</p>
        <div className="space-y-3">
          <input className={inputCls} style={inputStyle} placeholder="Nombre de la empresa" value={p.form.nombre} onChange={(e) => p.setForm({ ...p.form, nombre: e.target.value })} data-testid="closer-empresa-nombre" />
          <input className={inputCls} style={inputStyle} placeholder="Rubro" value={p.form.rubro} onChange={(e) => p.setForm({ ...p.form, rubro: e.target.value })} data-testid="closer-empresa-rubro" />
          <div className="grid grid-cols-2 gap-3">
            <input className={inputCls} style={inputStyle} placeholder="Tamaño" value={p.form.tamano} onChange={(e) => p.setForm({ ...p.form, tamano: e.target.value })} data-testid="closer-empresa-tamano" />
            <input className={inputCls} style={inputStyle} placeholder="País (ej: Argentina)" value={p.form.pais} onChange={(e) => p.setForm({ ...p.form, pais: e.target.value })} data-testid="closer-empresa-pais" />
          </div>
          <div>
            <label className="font-sans block mb-1.5" style={{ fontSize: 12, color: "#8A8A8A" }}>Cómo le habla la plataforma</label>
            <select className={inputCls} style={inputStyle} value={p.form.variante_trato} onChange={(e) => p.setForm({ ...p.form, variante_trato: e.target.value })} data-testid="closer-empresa-variante">
              <option value="">Automático por país (Argentina/Uruguay → vos · resto → tú)</option>
              <option value="vos">Forzar vos (rioplatense)</option>
              <option value="tu">Forzar tú (neutro)</option>
            </select>
          </div>
          <div>
            <label className="font-sans block mb-1.5" style={{ fontSize: 12, color: "#8A8A8A" }}>Link del grupo de WhatsApp con el cliente</label>
            <input className={inputCls} style={inputStyle} placeholder="https://chat.whatsapp.com/… (para cuando pida ayuda)" value={p.form.link_grupo_whatsapp} onChange={(e) => p.setForm({ ...p.form, link_grupo_whatsapp: e.target.value })} data-testid="closer-empresa-whatsapp" />
          </div>
          <textarea className={inputCls} style={{ ...inputStyle, minHeight: 140, resize: "vertical" }} placeholder="Lo que el asistente tiene que saber (qué hacen, herramientas, qué les duele, qué prometimos, quién decide)…" value={p.form.contexto_venta} onChange={(e) => p.setForm({ ...p.form, contexto_venta: e.target.value })} data-testid="closer-contexto" />
        </div>

        <div className="font-sans mt-6 mb-1" style={{ fontSize: 13, color: "#8A8A8A", fontWeight: 500 }}>Las personas que van a entrar</div>
        <p className="font-sans mb-3" style={{ fontSize: 12, color: "#7A7A7A" }}>El rol y el contexto son opcionales, pero ayudan: el asistente saluda a cada uno por su rol.</p>
        <div className="space-y-3">
          {p.usuarios.map((u, i) => (
            <div key={i} className="rounded-lg p-3 space-y-2" style={{ border: "1px solid #262626" }} data-testid={`closer-usuario-${i}`}>
              <div className="flex gap-2 items-center">
                <input className={inputCls + " !py-2"} style={inputStyle} placeholder="Nombre" value={u.nombre} onChange={(e) => p.setUsuario(i, "nombre", e.target.value)} data-testid={`closer-usuario-nombre-${i}`} />
                <input className={inputCls + " !py-2"} style={inputStyle} placeholder="Mail" value={u.email} onChange={(e) => p.setUsuario(i, "email", e.target.value)} data-testid={`closer-usuario-email-${i}`} />
                {p.usuarios.length > 1 && <button className="text-gris hover:text-rojo p-2" onClick={() => p.delUsuario(i)} data-testid={`closer-usuario-del-${i}`}><Trash2 size={16} /></button>}
              </div>
              <div className="flex gap-2 items-center">
                {(() => {
                  const esOtro = !!p.rolOtro[i] || (u.rol_cargo && !ROLES_BASE.includes(u.rol_cargo));
                  return (
                    <div className="flex-1 flex gap-2">
                      <select className={inputCls + " !py-2"} style={inputStyle}
                        value={esOtro ? "otro" : (u.rol_cargo || "")}
                        onChange={(e) => { const val = e.target.value; if (val === "otro") { p.setRolOtro((x) => ({ ...x, [i]: true })); p.setUsuario(i, "rol_cargo", ""); } else { p.setRolOtro((x) => ({ ...x, [i]: false })); p.setUsuario(i, "rol_cargo", val); } }}
                        data-testid={`closer-usuario-rol-${i}`}>
                        <option value="">Rol (opcional)</option>
                        {ROLES_BASE.map((r) => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                        <option value="otro">Otro…</option>
                      </select>
                      {esOtro && <input className={inputCls + " !py-2 flex-1"} style={inputStyle} placeholder="¿Cuál?" value={u.rol_cargo} onChange={(e) => p.setUsuario(i, "rol_cargo", e.target.value)} data-testid={`closer-usuario-rol-otro-${i}`} />}
                    </div>
                  );
                })()}
                <input className={inputCls + " !py-2 flex-1"} style={inputStyle} placeholder="Contexto (opcional)" value={u.contexto} onChange={(e) => p.setUsuario(i, "contexto", e.target.value)} data-testid={`closer-usuario-contexto-${i}`} />
              </div>
            </div>
          ))}
        </div>
        <button className="font-sans mt-3 flex items-center gap-1.5" style={{ fontSize: 13, color: "#C9A84C" }} onClick={p.addUsuario} data-testid="closer-add-usuario"><Plus size={14} /> Sumar persona</button>

        <div className="mt-6"><BtnGold onClick={p.crearCliente} disabled={p.creando || !p.formCompleto} full testid="closer-crear-cliente">{p.creando ? "Creando…" : "Crear cliente y mandar acceso"}</BtnGold></div>
        {!p.formCompleto && <p className="font-sans mt-2" style={{ fontSize: 12, color: "#7A7A7A" }} data-testid="closer-faltan-datos">Completá todos los datos de la empresa y de cada persona para crear el acceso.</p>}

        {p.accesos.length > 0 && (
          <div className="mt-5 space-y-2">
            <div className="font-sans" style={{ fontSize: 13, color: "#0CA30C", fontWeight: 500 }}>Accesos generados</div>
            {p.accesos.map((a, i) => (
              <div key={i} className="rounded-lg p-3 flex items-center justify-between gap-2" style={{ border: "1px solid #262626" }} data-testid={`acceso-${i}`}>
                <div className="min-w-0">
                  <div className="font-sans" style={{ fontSize: 14, color: "#E8E8E8" }}>{a.nombre}{a.rol_cargo ? <span style={{ color: "#7A7A7A" }}> · {a.rol_cargo}</span> : null}</div>
                  <div className="font-sans truncate" style={{ fontSize: 11, color: "#7A7A7A" }}>{a.email}</div>
                  <div className="font-sans truncate mt-1" style={{ fontSize: 11, color: "#7A7A7A" }} data-testid={`acceso-link-${i}`}>{p.linkDe(a)}</div>
                </div>
                <BtnGhost onClick={() => p.copiarLink(p.linkDe(a))} testid={`acceso-copiar-${i}`}><Copy size={13} /> Copiar</BtnGhost>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ---------- CLOSERS (admin) ----------
function VistaClosers({ resumen, closer, setCloser, crearCloser, candado, llmSalud, mailSalud, vida, permisos, puertasAdmin }) {
  const closers = resumen?.closers || [];
  const inputStyle = { background: "#0F0F0F", border: "1px solid #262626", color: "#E8E8E8", fontSize: 14 };
  const pct30 = vida && vida.tasa_30 != null ? Math.round(vida.tasa_30 * 100) : null;
  return (
    <div className="space-y-8">
      <Card className="p-6" data-testid="puertas-card">
        <div className="font-sans mb-1 flex items-center gap-2" style={{ fontSize: 14, color: "#F5F0E8", fontWeight: 500 }}>
          <Award size={15} strokeWidth={1.7} /> Las Puertas del Olimpo · orgullo de la cuenta
        </div>
        <p className="font-sans mb-5" style={{ fontSize: 13, color: "#8A8A8A" }}>
          Cuántas cuentas llegaron lejos y cuántas descargaron su sello. Nadie comparte un certificado si no está orgulloso: sirve de termómetro.
        </p>
        <div className="grid grid-cols-3 gap-6">
          <div data-testid="puertas-abiertas">
            <Hero>{puertasAdmin ? fmtInt(puertasAdmin.puertas_abiertas) : "—"}</Hero>
            <Etiqueta>cuentas que abrieron las puertas</Etiqueta>
          </div>
          <div data-testid="puertas-sellos">
            <Hero>{puertasAdmin ? fmtInt(puertasAdmin.sellos_descargados) : "—"}</Hero>
            <Etiqueta>sellos descargados</Etiqueta>
          </div>
          <div data-testid="puertas-cuentas-sello">
            <Hero>{puertasAdmin ? fmtInt(puertasAdmin.cuentas_con_sello) : "—"}</Hero>
            <Etiqueta>cuentas que descargaron</Etiqueta>
          </div>
        </div>
      </Card>

      <Card className="p-6" data-testid="permisos-card">
        <div className="font-sans mb-1 flex items-center gap-2" style={{ fontSize: 14, color: "#F5F0E8", fontWeight: 500 }}>
          <AlertTriangle size={15} strokeWidth={1.7} /> Frenadas por plan · info nuestra, el cliente no la ve
        </div>
        <p className="font-sans mb-5" style={{ fontSize: 13, color: "#8A8A8A" }}>
          Cuentas sin plan pago o que no saben cuál tienen. Si son muchas, algo no se aclara bien en la venta.
        </p>
        <div className="grid grid-cols-2 gap-6 mb-5">
          <div data-testid="permisos-frenadas-total">
            <Hero>{permisos ? fmtInt(permisos.frenadas_total) : "—"}</Hero>
            <Etiqueta>cuentas frenadas por plan</Etiqueta>
          </div>
        </div>
        {permisos && permisos.frenadas_por_plan.length > 0 && (
          <div className="space-y-1 mb-5">
            {permisos.frenadas_por_plan.map((f, i) => (
              <div key={i} className="flex items-center justify-between py-1" style={{ borderBottom: "1px solid #1A1A1A" }} data-testid={`permisos-frenada-${i}`}>
                <span className="font-sans truncate" style={{ fontSize: 13, color: "#E8E8E8" }}>{f.empresa}{f.persona ? ` · ${f.persona}` : ""}</span>
                <span className="font-sans ml-3" style={{ fontSize: 12, color: "#EC835A" }}>{f.plan_label}</span>
              </div>
            ))}
          </div>
        )}
        <Etiqueta>conectores donde más se traban</Etiqueta>
        {!permisos || permisos.conectores_trabados.length === 0 ? (
          <div className="font-sans mt-2" style={{ fontSize: 13, color: "#7A7A7A" }} data-testid="permisos-conectores-vacio">
            Nadie se trabó conectando una herramienta todavía.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2 mt-2">
            {permisos.conectores_trabados.map((c) => (
              <span key={c.conector} className="font-sans px-3 py-1 rounded-full" style={{ fontSize: 12, color: "#E8E8E8", border: "1px solid #262626" }} data-testid={`permisos-conector-${c.conector}`}>{c.conector} · {c.veces}</span>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-6" data-testid="vida-card">
        <div className="font-sans mb-1 flex items-center gap-2" style={{ fontSize: 14, color: "#F5F0E8", fontWeight: 500 }}>
          <RefreshCw size={15} strokeWidth={1.7} /> Chequeo de vida · procesos que siguen vivos
        </div>
        <p className="font-sans mb-5" style={{ fontSize: 13, color: "#8A8A8A" }}>
          Lo que confirmamos a los 14 y 30 días de poner un proceso a andar. Es lo que hace que las horas recuperadas sean un hecho y no una promesa.
        </p>
        <div className="grid grid-cols-2 gap-6">
          <div data-testid="vida-vivos-30">
            <Hero>{pct30 == null ? "—" : `${pct30}%`}</Hero>
            <Etiqueta>siguen vivos a los 30 días</Etiqueta>
            <div className="font-sans mt-1" style={{ fontSize: 12, color: "#7A7A7A" }}>
              {vida ? `${vida.vivos_30} de ${vida.total_30} chequeados` : ""}
            </div>
          </div>
          <div data-testid="vida-abandonos">
            <Hero>{vida ? fmtInt(vida.abandonos_total) : "—"}</Hero>
            <Etiqueta>procesos que dejaron</Etiqueta>
          </div>
        </div>
        <div className="mt-6">
          <Etiqueta>motivos de abandono</Etiqueta>
          {!vida || vida.motivos.length === 0 ? (
            <div className="font-sans mt-2" style={{ fontSize: 13, color: "#7A7A7A" }} data-testid="vida-motivos-vacio">
              Todavía nadie dejó un proceso. Buena señal.
            </div>
          ) : (
            <div className="space-y-1 mt-2">
              {vida.motivos.map((m) => (
                <div key={m.motivo} className="flex items-center justify-between py-1" style={{ borderBottom: "1px solid #1A1A1A" }} data-testid={`vida-motivo-${m.motivo}`}>
                  <span className="font-sans truncate" style={{ fontSize: 13, color: "#E8E8E8" }}>{m.label}</span>
                  <Dato className="text-sm ml-3">{m.veces}</Dato>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      <Card>
        <div className="px-5 py-4 font-sans" style={{ fontSize: 13, color: "#8A8A8A", fontWeight: 500 }}>Todos los closers</div>
        {closers.length === 0 ? <EmptyState icon={Users} titulo="Todavía no hay closers." sub="Sumá el primero abajo." /> : closers.map((cl) => (
          <div key={cl.id} className="flex items-center px-5" style={{ minHeight: 56, borderTop: "1px solid #1A1A1A" }} data-testid={`closer-fila-${cl.id}`}>
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <Avatar nombre={cl.nombre} size={34} />
              <div className="min-w-0">
                <div className="font-sans truncate" style={{ fontSize: 14, color: "#F5F0E8", fontWeight: 500 }}>{cl.nombre}</div>
                <div className="font-sans truncate" style={{ fontSize: 12, color: "#7A7A7A" }}>{cl.email}</div>
              </div>
            </div>
            <div style={{ width: 90 }} className="text-right"><Dato className="text-sm">{fmtInt(cl.cuentas)}</Dato><div><Etiqueta>cuentas</Etiqueta></div></div>
            <div style={{ width: 90 }} className="text-right"><Dato className="text-sm">{fmtInt(cl.entraron)}</Dato><div><Etiqueta>entraron</Etiqueta></div></div>
            <div style={{ width: 90 }} className="text-right"><Dato className="text-sm">{fmtInt(cl.activas)}</Dato><div><Etiqueta>activas</Etiqueta></div></div>
          </div>
        ))}
      </Card>

      <Card className="p-6 max-w-md">
        <div className="font-sans mb-4" style={{ fontSize: 14, color: "#F5F0E8", fontWeight: 500 }}>Sumar un closer</div>
        <form onSubmit={crearCloser} className="space-y-3">
          <input className="w-full rounded-lg font-sans px-3 py-2.5" style={inputStyle} placeholder="Nombre" value={closer.nombre} onChange={(e) => setCloser({ ...closer, nombre: e.target.value })} required data-testid="admin-closer-nombre" />
          <input className="w-full rounded-lg font-sans px-3 py-2.5" style={inputStyle} type="email" placeholder="Mail" value={closer.email} onChange={(e) => setCloser({ ...closer, email: e.target.value })} required data-testid="admin-closer-email" />
          <input className="w-full rounded-lg font-sans px-3 py-2.5" style={inputStyle} type="password" placeholder="Contraseña" value={closer.password} onChange={(e) => setCloser({ ...closer, password: e.target.value })} required minLength={6} data-testid="admin-closer-password" />
          <BtnGold full testid="admin-crear-closer" type="submit">Crear closer</BtnGold>
        </form>
      </Card>

      <Card className="p-6" data-testid="llm-salud-card">
        <div className="font-sans mb-1 flex items-center gap-2" style={{ fontSize: 14, color: "#F5F0E8", fontWeight: 500 }}><Lock size={15} strokeWidth={1.7} /> Llamadas al modelo · fallos de las últimas 24 h</div>
        <p className="font-sans mb-4" style={{ fontSize: 13, color: "#8A8A8A" }}>Cuántas veces por día falla el modelo y por qué, cuántas se resolvieron solas con reintento y cuántas llegaron al cliente. De esto depende qué plan conviene contratar.</p>
        {!llmSalud ? (
          <div className="font-sans" style={{ fontSize: 13, color: "#7A7A7A" }} data-testid="llm-salud-vacio">Sin datos todavía.</div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Etiqueta>fallos totales</Etiqueta>
                <Dato className="text-2xl" data-testid="llm-salud-total">{llmSalud.total_fallos}</Dato>
              </div>
              <div>
                <Etiqueta>resueltos con reintento</Etiqueta>
                <Dato className="text-2xl" data-testid="llm-salud-reintentos">{llmSalud.resueltos_con_reintento}</Dato>
              </div>
              <div>
                <Etiqueta>llegaron al cliente</Etiqueta>
                <Dato className="text-2xl" style={{ color: llmSalud.llegaron_al_cliente > 0 ? "#E07A5F" : undefined }} data-testid="llm-salud-cliente">{llmSalud.llegaron_al_cliente}</Dato>
              </div>
            </div>
            <div className="font-sans" style={{ fontSize: 12, color: "#7A7A7A" }}>Límite de llamadas simultáneas actual: {llmSalud.concurrencia_max}</div>
            {llmSalud.por_causa && llmSalud.por_causa.length > 0 && (
              <div>
                <Etiqueta>por causa</Etiqueta>
                <div className="flex flex-wrap gap-2 mt-2">
                  {llmSalud.por_causa.map((c) => (
                    <span key={c.causa} className="font-sans px-3 py-1 rounded-full" style={{ fontSize: 12, color: "#E8E8E8", border: "1px solid #262626" }} data-testid={`llm-causa-${c.causa}`}>{c.causa} · {c.veces}</span>
                  ))}
                </div>
              </div>
            )}
            {llmSalud.total_fallos === 0 && (
              <div className="font-sans" style={{ fontSize: 13, color: "#7A7A7A" }} data-testid="llm-salud-cero">Ningún fallo en las últimas 24 h.</div>
            )}
          </div>
        )}
      </Card>

      <Card className="p-6" data-testid="mail-salud-card">
        <div className="font-sans mb-1 flex items-center gap-2" style={{ fontSize: 14, color: "#F5F0E8", fontWeight: 500 }}><Lock size={15} strokeWidth={1.7} /> Mails de recuperación · últimas 24 h</div>
        <p className="font-sans mb-4" style={{ fontSize: 13, color: "#8A8A8A" }}>Cuántos mails de "olvidé mi contraseña" salieron bien y cuántos rechazó el proveedor. Si algo falla, queda acá y no en silencio.</p>
        {!mailSalud ? (
          <div className="font-sans" style={{ fontSize: 13, color: "#7A7A7A" }} data-testid="mail-salud-vacio">Sin datos todavía.</div>
        ) : (
          <div className="space-y-5">
            {!mailSalud.configurado && (
              <div className="font-sans px-3 py-2 rounded-lg" style={{ fontSize: 13, color: "#E07A5F", background: "#1A1010", border: "1px solid #3A1F1F" }} data-testid="mail-salud-sin-config">
                Falta cargar RESEND_API_KEY: hoy no se manda ningún mail real.
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Etiqueta>mails enviados</Etiqueta>
                <Dato className="text-2xl" data-testid="mail-salud-enviados">{mailSalud.enviados}</Dato>
              </div>
              <div>
                <Etiqueta>fallos</Etiqueta>
                <Dato className="text-2xl" style={{ color: mailSalud.total_fallos > 0 ? "#E07A5F" : undefined }} data-testid="mail-salud-fallos">{mailSalud.total_fallos}</Dato>
              </div>
            </div>
            {mailSalud.total_fallos === 0 ? (
              <div className="font-sans" style={{ fontSize: 13, color: "#7A7A7A" }} data-testid="mail-salud-cero">Ningún fallo en las últimas 24 h.</div>
            ) : (
              <div className="space-y-2" data-testid="mail-salud-lista">
                {mailSalud.ultimos.map((f, i) => (
                  <div key={i} className="font-sans px-3 py-2 rounded-lg" style={{ fontSize: 12, color: "#C8C8C8", background: "#0F0F0F", border: "1px solid #262626" }}>
                    <span style={{ color: "#E8E8E8" }}>{f.email}</span> · {f.detalle}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      <Card className="p-6" data-testid="candado-doc-card">
        <div className="font-sans mb-1 flex items-center gap-2" style={{ fontSize: 14, color: "#F5F0E8", fontWeight: 500 }}><Lock size={15} strokeWidth={1.7} /> Candado del pedido · palabras que se filtraron</div>
        <p className="font-sans mb-4" style={{ fontSize: 13, color: "#8A8A8A" }}>Cada vez que el generador escribió una palabra prohibida y hubo que regenerar o cambiar la forma.</p>
        {!candado || candado.total === 0 ? (
          <div className="font-sans" style={{ fontSize: 13, color: "#7A7A7A" }} data-testid="candado-doc-vacio">Todavía no se filtró ninguna palabra. El candado no tuvo que actuar.</div>
        ) : (
          <div className="space-y-5">
            <div className="font-sans" style={{ fontSize: 14, color: "#E8E8E8" }} data-testid="candado-doc-total">{candado.total} regeneraciones en total</div>
            <div>
              <Etiqueta>por palabra</Etiqueta>
              <div className="flex flex-wrap gap-2 mt-2">
                {candado.por_termino.map((t) => (
                  <span key={t.termino} className="font-sans px-3 py-1 rounded-full" style={{ fontSize: 12, color: "#E8E8E8", border: "1px solid #262626" }} data-testid={`candado-termino-${t.termino}`}>{t.termino} · {t.veces}</span>
                ))}
              </div>
            </div>
            <div>
              <Etiqueta>por proceso</Etiqueta>
              <div className="space-y-1 mt-2">
                {candado.por_proceso.map((pp) => (
                  <div key={pp.proceso_id} className="flex items-center justify-between py-1" style={{ borderBottom: "1px solid #1A1A1A" }} data-testid={`candado-proceso-${pp.proceso_id}`}>
                    <span className="font-sans truncate" style={{ fontSize: 13, color: "#E8E8E8" }}>{pp.proceso_nombre || pp.proceso_id}</span>
                    <Dato className="text-sm ml-3">{pp.veces}</Dato>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

// ---------- Ficha wrapper con acciones del closer ----------
function FichaCuentaWrap({ cuenta, onClose, onRecordatorio, onCopiarLink, esAdmin, closers = [], onAsignado, onCambio }) {
  const [asignando, setAsignando] = React.useState(false);
  const asignar = async (email) => {
    setAsignando(true);
    try {
      await api.patch(`/admin/empresa/${cuenta.id}/asignar-closer`, { email });
      toast.success(email ? "Closer asignado." : "Se quitó el closer.");
      onAsignado && onAsignado();
      onClose && onClose();
    } catch (e) { toast.error("No pude asignar el closer."); }
    finally { setAsignando(false); }
  };
  return (
    <div>
      <FichaCuenta cuenta={cuenta} onClose={onClose} onCambio={onCambio} />
      <div className="fixed z-[110] flex gap-2 items-center" style={{ right: 24, bottom: 24 }}>
        {esAdmin && (
          <select value={cuenta.cargada_por || ""} disabled={asignando} onChange={(e) => asignar(e.target.value)} data-testid={`ficha-asignar-closer-${cuenta.id}`}
            className="font-sans px-3 py-2.5 rounded-lg" style={{ background: "#161616", border: `1px solid ${cuenta.cargada_por ? "#2A2A2A" : "#C9A84C"}`, color: cuenta.cargada_por ? "#E8E8E8" : "#C9A84C", fontSize: 13 }}>
            <option value="">Sin closer asignado</option>
            {closers.map((c) => <option key={c.email} value={c.email}>{c.nombre || c.email}</option>)}
          </select>
        )}
        <button onClick={() => onCopiarLink(cuenta)} className="font-sans flex items-center gap-2 px-4 py-2.5 rounded-lg" style={{ background: "#161616", border: "1px solid #2A2A2A", color: "#E8E8E8", fontSize: 13 }} data-testid={`ficha-copiar-link-${cuenta.id}`}><Copy size={14} /> Copiar link</button>
        <button onClick={() => onRecordatorio(cuenta)} className="font-sans flex items-center gap-2 px-4 py-2.5 rounded-lg" style={{ background: "#C9A84C", color: "#0A0A0A", fontSize: 13, fontWeight: 500 }} data-testid={`ficha-recordatorio-${cuenta.id}`}><Send size={14} /> Mandar recordatorio</button>
      </div>
    </div>
  );
}

function BtnGhost({ children, onClick, disabled, testid, ...r }) {
  return <button onClick={onClick} disabled={disabled} data-testid={testid} className="font-sans flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50" style={{ background: "transparent", border: "1px solid #2A2A2A", color: "#D8D8D8", fontSize: 13 }} {...r}>{children}</button>;
}
function BtnGold({ children, onClick, disabled, full, testid, type = "button" }) {
  return <button type={type} onClick={onClick} disabled={disabled} data-testid={testid} className={`font-sans flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg transition-opacity disabled:opacity-40 disabled:cursor-not-allowed ${full ? "w-full" : ""}`} style={{ background: "#C9A84C", color: "#0A0A0A", fontSize: 14, fontWeight: 500 }}>{children}</button>;
}

function Cargando() {
  return <div className="flex items-center justify-center py-20"><div className="w-10 h-10 rounded-full animate-breathe" style={{ background: "radial-gradient(circle,#C9A84C,#977947)" }} /></div>;
}


function VistaOportunidades({ onSel, cuentas }) {
  const [ops, setOps] = useState(null);
  const cargar = async () => {
    try { const { data } = await api.get("/admin/oportunidades"); setOps(data.oportunidades || []); }
    catch { setOps([]); }
  };
  React.useEffect(() => { cargar(); }, []);
  const cambiar = async (id, estado) => {
    try { await api.patch(`/admin/oportunidad/${id}`, { estado }); toast.success("Actualizado."); cargar(); }
    catch { toast.error("No pude actualizar."); }
  };
  const ESTADOS = { abierta: "#C9A84C", en_conversacion: "#EC835A", ganada: "#0CA30C", descartada: "#6B6B6B" };
  if (ops === null) return <div className="font-sans py-16 text-center" style={{ color: "#6B6B6B" }}>Cargando…</div>;
  return (
    <div data-testid="vista-oportunidades">
      <div className="font-sans mb-4" style={{ fontSize: 13, color: "#8A8A8A" }}>
        Casos donde ninguna herramienta del catálogo cubría bien el proceso. Cada uno es una oportunidad de desarrollo para Olimpo.
      </div>
      {ops.length === 0 ? (
        <div className="font-sans py-16 text-center" style={{ color: "#6B6B6B" }} data-testid="oportunidades-vacio">Todavía no hay oportunidades marcadas.</div>
      ) : ops.map((o) => (
        <div key={o.id} className="rounded-lg p-4 mb-3" style={{ background: "#111", border: "1px solid #1F1F1F" }} data-testid={`oportunidad-${o.id}`}>
          <div className="flex items-center justify-between mb-2">
            <button onClick={() => { const c = (cuentas || []).find((x) => x.id === o.empresa_id); if (c) onSel(c); }} className="font-serif hover:underline" style={{ fontSize: 16, color: "#F5F0E8" }}>{o.empresa}</button>
            <span className="font-sans" style={{ fontSize: 11, color: ESTADOS[o.estado] || "#8A8A8A", textTransform: "uppercase", letterSpacing: "0.04em" }}>{(o.estado || "").replace("_", " ")}</span>
          </div>
          <div className="font-sans mb-1" style={{ fontSize: 13, color: "#D8D8D8" }}><b>Proceso:</b> {o.proceso_nombre}</div>
          <div className="font-sans mb-1" style={{ fontSize: 13, color: "#D8D8D8" }}><b>El problema:</b> {o.problema}</div>
          <div className="font-sans mb-3" style={{ fontSize: 13, color: "#B8B8B8" }}><b>Qué haría falta:</b> {o.que_haria_falta}</div>
          <div className="flex gap-2">
            {["en_conversacion", "ganada", "descartada"].map((e) => (
              <button key={e} onClick={() => cambiar(o.id, e)} className="font-sans px-3 py-1.5 rounded-md" style={{ fontSize: 12, background: "#161616", border: "1px solid #2A2A2A", color: "#D8D8D8" }} data-testid={`oportunidad-${o.id}-${e}`}>
                {e === "en_conversacion" ? "En conversación" : e === "ganada" ? "Ganada" : "Descartar"}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function VistaVideos() {
  const [grupos, setGrupos] = useState(null);
  const [chatUi, setChatUi] = useState(null);
  const [subiendo, setSubiendo] = useState("");
  const [urlDraft, setUrlDraft] = useState({});
  const [guardandoUrl, setGuardandoUrl] = useState("");
  const cargar = async () => {
    try { const { data } = await api.get("/admin/videos"); setGrupos(data.grupos || []); setChatUi({ fecha: data.chatgpt_ui_verificado, dias: data.chatgpt_ui_dias }); } catch { setGrupos([]); }
  };
  React.useEffect(() => { cargar(); }, []);
  const subir = async (pantalla, forma, paso_key, os, file) => {
    if (!file) return;
    if (!["video/mp4", "video/webm"].includes(file.type) && !/\.(mp4|webm)$/i.test(file.name)) { toast.error("Solo mp4 o webm"); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error("El clip pesa más de 20 MB"); return; }
    const slot = `${pantalla}|${forma || ""}|${paso_key}|${os || ""}`;
    setSubiendo(slot);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("pantalla", pantalla); fd.append("paso_key", paso_key);
      if (forma) fd.append("forma", forma);
      if (os) fd.append("os", os);
      await api.post("/admin/videos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Clip subido."); cargar();
    } catch (e) { toast.error("No pude subir el clip."); }
    finally { setSubiendo(""); }
  };
  const guardarUrl = async (pantalla, paso_key, os) => {
    const dk = `${paso_key}|${os || ""}`;
    const url = (urlDraft[dk] || "").trim();
    if (!url) { toast.error("Pegá una URL de Loom, YouTube o Vimeo."); return; }
    setGuardandoUrl(dk);
    try {
      const body = { pantalla, paso_key, url };
      if (os) body.os = os;
      await api.post("/admin/videos/url", body);
      toast.success("Video actualizado. Los clientes ya ven el nuevo.");
      setUrlDraft((d) => ({ ...d, [dk]: "" }));
      cargar();
    } catch (e) { toast.error(errText(e) || "No pude guardar la URL."); }
    finally { setGuardandoUrl(""); }
  };
  const borrar = async (id) => {
    try { await api.delete(`/admin/videos/${id}`); toast.success("Clip borrado."); cargar(); }
    catch { toast.error("No pude borrar."); }
  };
  const Slot = ({ g, p, os, label }) => {
    const key = os ? os : "generico";
    const cur = (p.clips || {})[key];
    const slotId = `${g.pantalla}|${g.forma || ""}|${p.paso_key}|${os || ""}`;
    const ref = React.useRef(null);
    return (
      <div className="flex items-center gap-2">
        {label && <span className="mono-label text-gris" style={{ fontSize: "9px", width: 60 }}>{label}</span>}
        <input ref={ref} type="file" accept=".mp4,.webm" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) subir(g.pantalla, g.forma, p.paso_key, os, f); e.target.value = ""; }} data-testid={`video-input-${slotId}`} />
        {cur ? (
          <>
            {cur.tipo === "url" ? (
              <div style={{ width: 160 }} data-testid={`video-preview-url-${slotId}`}>
                <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
                  <iframe src={cur.embed_url} title="preview" allowFullScreen className="absolute top-0 left-0 w-full h-full" style={{ borderRadius: 6, border: "1px solid #2A2A2A" }} />
                </div>
                <div className="mono-label text-gris mt-1" style={{ fontSize: 9 }}>{(cur.proveedor || "url").toUpperCase()}</div>
              </div>
            ) : (
              <video src={`${process.env.REACT_APP_BACKEND_URL}/api/clip-video/${cur.id}`} muted loop autoPlay playsInline style={{ width: 120, borderRadius: 6, border: "1px solid #2A2A2A" }} />
            )}
            <button onClick={() => ref.current?.click()} className="font-sans px-2 py-1 rounded" style={{ fontSize: 11, background: "#161616", border: "1px solid #2A2A2A", color: "#D8D8D8" }} data-testid={`video-reemplazar-${slotId}`}>Subir archivo</button>
            <button onClick={() => borrar(cur.id)} className="font-sans px-2 py-1 rounded" style={{ fontSize: 11, background: "#161616", border: "1px solid #3a2020", color: "#E0A0A0" }} data-testid={`video-borrar-${slotId}`}><Trash2 size={12} /></button>
          </>
        ) : (
          <button onClick={() => ref.current?.click()} disabled={subiendo === slotId} className="font-sans px-3 py-1.5 rounded flex items-center gap-1" style={{ fontSize: 12, background: "#C9A84C", color: "#0A0A0A", fontWeight: 500 }} data-testid={`video-subir-${slotId}`}>
            <Upload size={12} /> {subiendo === slotId ? "Subiendo…" : "Subir clip"}
          </button>
        )}
      </div>
    );
  };
  const UrlEditor = ({ pantalla, paso_key, os, label }) => {
    const dk = `${paso_key}|${os || ""}`;
    return (
    <div className="mt-3 pt-3" style={{ borderTop: "1px dashed #2A2A2A" }}>
      <div className="font-sans mb-1.5" style={{ fontSize: 11, color: "#8A8A8A" }}>{label ? `${label} · ` : ""}…o pegá una URL de Loom, YouTube o Vimeo (no hace falta subir un archivo):</div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={urlDraft[dk] || ""}
          onChange={(e) => setUrlDraft((d) => ({ ...d, [dk]: e.target.value }))}
          onKeyDown={(e) => { if (e.key === "Enter") guardarUrl(pantalla, paso_key, os); }}
          placeholder="https://www.loom.com/share/…"
          className="flex-1 font-sans px-3 py-2 rounded"
          style={{ fontSize: 13, background: "#0E0E0E", border: "1px solid #2A2A2A", color: "#E8E8E8" }}
          data-testid={`video-url-input-${dk}`}
        />
        <button onClick={() => guardarUrl(pantalla, paso_key, os)} disabled={guardandoUrl === dk} className="font-sans px-3 py-2 rounded shrink-0" style={{ fontSize: 12, background: "#C9A84C", color: "#0A0A0A", fontWeight: 500 }} data-testid={`video-url-guardar-${dk}`}>
          {guardandoUrl === dk ? "Guardando…" : "Guardar URL"}
        </button>
      </div>
    </div>
    );
  };
  if (grupos === null) return <div className="font-sans py-16 text-center" style={{ color: "#6B6B6B" }}>Cargando…</div>;
  return (
    <div data-testid="vista-videos">
      <div className="font-sans mb-4" style={{ fontSize: 13, color: "#8A8A8A" }}>Un clip corto por paso (mp4/webm, máx 20 MB) o una URL de Loom, YouTube o Vimeo. Los pasos de la Preparación aparecen dentro de la implementación y se comparten entre las formas que los usan. Los pasos que dependen del sistema operativo tienen dos ranuras (Mac / Windows).</div>
      {chatUi?.fecha && (
        (chatUi.dias != null && chatUi.dias > 60) ? (
          <div className="font-sans mb-4" style={{ fontSize: 13, color: "#C9A84C" }} data-testid="chatgpt-ui-aviso">
            Textos de ChatGPT verificados el {chatUi.fecha}. Hace más de dos meses. Revisar Complementos, Acceso completo, el complemento de Chrome y el botón de stop contra la app actual, y actualizar la fecha.
          </div>
        ) : (
          <div className="font-sans mb-4" style={{ fontSize: 13, color: "#8A8A8A" }} data-testid="chatgpt-ui-fecha">
            Textos de ChatGPT verificados el {chatUi.fecha}.
          </div>
        )
      )}
      {grupos.map((g) => (
        <div key={`${g.pantalla}-${g.forma}`} className="mb-6" data-testid={`video-grupo-${g.pantalla}-${g.forma || "x"}`}>
          <div className="font-serif mb-2" style={{ fontSize: 16, color: "#C9A84C" }}>{g.titulo}</div>
          {g.pasos.map((p) => {
            const aceptaUrl = g.pantalla === "preparacion" || g.pantalla === "conectores_extra" || (g.pantalla === "global" && (p.paso_key === "bienvenida" || p.paso_key === "preparar_intro" || p.paso_key === "empleado_nuevo"));
            return (
              <div key={p.paso_key} className="rounded-lg p-3 mb-2" style={{ background: "#111", border: "1px solid #1F1F1F" }}>
                <div className="font-sans mb-2" style={{ fontSize: 13, color: "#E8E8E8" }}>{p.label}</div>
                {p.os_dependiente ? (
                  <div className="flex flex-col gap-2">
                    <Slot g={g} p={p} os="mac" label="Mac" />
                    <Slot g={g} p={p} os="windows" label="Windows" />
                  </div>
                ) : <Slot g={g} p={p} os={null} label="" />}
                {aceptaUrl && (p.os_dependiente ? (
                  <>
                    <UrlEditor pantalla={g.pantalla} paso_key={p.paso_key} os="mac" label="Mac" />
                    <UrlEditor pantalla={g.pantalla} paso_key={p.paso_key} os="windows" label="Windows" />
                  </>
                ) : <UrlEditor pantalla={g.pantalla} paso_key={p.paso_key} os={null} label="" />)}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}


// ---------- PRODUCTO · medición de los videos (PROMPT 3) ----------
const PANTALLA_LABEL = { global: "Video narrado", preparar: "Preparar", implementacion: "Implementar" };

function VistaProducto() {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/videos-metricas"); setData(data); }
      catch { setData({ clips: [], bienvenida: {}, presentacion: {} }); }
    })();
  }, []);

  if (data === null) return <div className="font-sans py-16 text-center" style={{ color: "#6B6B6B" }}>Cargando…</div>;

  const b = data.bienvenida || {};
  const p = data.presentacion || {};
  const clips = data.clips || [];
  const conDatos = clips.length > 0 || (b.vistas || 0) > 0 || (p.reproducciones || 0) > 0;

  return (
    <div data-testid="vista-producto">
      <div className="font-sans mb-6" style={{ fontSize: 13, color: "#8A8A8A" }}>
        Cómo se usan los videos. El video no es la solución, es el síntoma: si un clip se repite más de tres veces en promedio, ese paso está mal explicado en el texto.
      </div>

      {!conDatos && (
        <Card className="p-8"><EmptyState icon={Activity} titulo="Todavía no hay datos de uso." sub="En cuanto los clientes miren los videos, acá vas a ver qué clips se repiten y cómo funciona la bienvenida." /></Card>
      )}

      {/* --- Ranking de clips que más se repiten --- */}
      {clips.length > 0 && (
        <div className="mb-10" data-testid="producto-clips">
          <div className="font-serif mb-1" style={{ fontSize: 18, color: "#C9A84C" }}>Clips que más se repiten</div>
          <div className="font-sans mb-4" style={{ fontSize: 12, color: "#8A8A8A" }}>Promedio de veces que cada persona lo mira en loop. Más de 3 = paso mal explicado (en rojo).</div>
          <div className="space-y-2">
            {clips.map((c, i) => (
              <div key={`${c.pantalla}-${c.forma || "x"}-${c.paso_key}`} className="rounded-lg p-4" data-testid={`producto-clip-${i}`}
                style={{ background: c.alerta ? "rgba(220,90,90,0.07)" : "#111", border: `1px solid ${c.alerta ? "rgba(220,90,90,0.5)" : "#1F1F1F"}` }}>
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-sans truncate" style={{ fontSize: 14, color: "#E8E8E8" }}>{c.label}</div>
                    <div className="font-sans mt-0.5" style={{ fontSize: 11, color: "#8A8A8A" }}>
                      {PANTALLA_LABEL[c.pantalla] || c.pantalla}{c.forma ? ` · Forma ${c.forma}` : ""} · {c.personas} {c.personas === 1 ? "persona" : "personas"} · {c.reproducciones} {c.reproducciones === 1 ? "reproducción" : "reproducciones"}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Repeat size={14} style={{ color: c.alerta ? "#E27B7B" : "#8A8A8A" }} />
                    <span className="font-mono tabular-nums" style={{ fontSize: 20, color: c.alerta ? "#E27B7B" : "#F5F0E8", fontWeight: 600 }} data-testid={`producto-clip-promedio-${i}`}>{c.promedio}</span>
                    <span className="font-sans" style={{ fontSize: 11, color: "#8A8A8A" }}>x</span>
                  </div>
                </div>
                {c.alerta && (
                  <div className="mt-3 rounded-md p-3" data-testid={`producto-clip-alerta-${i}`} style={{ background: "#0E0E0E", border: "1px solid #2A1A1A" }}>
                    <div className="font-sans mb-1 flex items-center gap-1.5" style={{ fontSize: 11, color: "#E27B7B", fontWeight: 500 }}>
                      <AlertTriangle size={12} /> Este paso se repite demasiado. Reescribí el texto:
                    </div>
                    <div className="font-sans whitespace-pre-line" style={{ fontSize: 13, color: "#C8C8C8", lineHeight: 1.5 }} data-testid={`producto-clip-texto-${i}`}>{c.texto_paso || "—"}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* --- Bienvenida --- */}
      {(b.mostrada || b.vistas || 0) > 0 && (
        <div className="mb-10" data-testid="producto-bienvenida">
          <div className="font-serif mb-3" style={{ fontSize: 18, color: "#C9A84C" }}>Video de bienvenida</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricBox label="Le dieron play" valor={b.play} sub={`de ${b.mostrada}`} />
            <MetricBox label="Lo terminaron" valor={b.terminada} sub={`de ${b.mostrada}`} />
            <MetricBox label="Lo saltearon" valor={b.salteada} sub={`de ${b.mostrada}`} />
            <MetricBox label="Segundo prom. de salteo" valor={b.segundo_promedio_salteo} sub="segundos" />
          </div>
          {b.alerta_arranque && (
            <div className="mt-3 rounded-md p-3 flex items-start gap-2" data-testid="producto-bienvenida-alerta" style={{ background: "rgba(220,90,90,0.07)", border: "1px solid rgba(220,90,90,0.5)" }}>
              <AlertTriangle size={14} style={{ color: "#E27B7B", marginTop: 1 }} />
              <div className="font-sans" style={{ fontSize: 13, color: "#E8C0C0", lineHeight: 1.5 }}>
                La saltean sistemáticamente en los primeros cinco segundos. El video arranca mal: hay que rehacerlo.
              </div>
            </div>
          )}
        </div>
      )}

      {/* --- Presentación de /preparar --- */}
      {(p.reproducciones || 0) > 0 && (
        <div className="mb-6" data-testid="producto-presentacion">
          <div className="font-serif mb-3" style={{ fontSize: 18, color: "#C9A84C" }}>Presentación de /preparar</div>
          <div className="grid grid-cols-3 gap-3">
            <MetricBox label="La abrieron" valor={p.abrieron} sub={p.abrieron === 1 ? "persona" : "personas"} />
            <MetricBox label="La ven entera" valor={p.enteras} sub={`de ${p.reproducciones}`} />
            <MetricBox label="La cierran antes" valor={p.cerradas_antes} sub={`de ${p.reproducciones}`} />
          </div>
        </div>
      )}
    </div>
  );
}

function MetricBox({ label, valor, sub }) {
  return (
    <div className="rounded-lg p-4" style={{ background: "#111", border: "1px solid #1F1F1F" }}>
      <div className="font-sans mb-1" style={{ fontSize: 11, color: "#8A8A8A", textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
      <div className="flex items-baseline gap-1.5">
        <span className="font-mono tabular-nums" style={{ fontSize: 26, color: "#F5F0E8", fontWeight: 600 }}>{valor ?? 0}</span>
        <span className="font-sans" style={{ fontSize: 12, color: "#8A8A8A" }}>{sub}</span>
      </div>
    </div>
  );
}



function VistaCatalogo() {
  const [cat, setCat] = useState(null);
  const cargar = async () => {
    try { const { data } = await api.get("/admin/catalogo"); setCat(data.catalogo || []); }
    catch { setCat([]); }
  };
  React.useEffect(() => { cargar(); }, []);
  const verificar = async (id) => {
    try { await api.patch(`/admin/catalogo/${id}`, { marcar_verificado: true }); toast.success("Marcada como verificada hoy."); cargar(); }
    catch { toast.error("No pude actualizar."); }
  };
  const toggleActiva = async (e) => {
    try { await api.patch(`/admin/catalogo/${e.id}`, { activa: !e.activa }); cargar(); }
    catch { toast.error("No pude actualizar."); }
  };
  if (cat === null) return <div className="font-sans py-16 text-center" style={{ color: "#6B6B6B" }}>Cargando…</div>;
  return (
    <div data-testid="vista-catalogo">
      <div className="font-sans mb-4" style={{ fontSize: 13, color: "#8A8A8A" }}>
        Las herramientas que el asistente puede proponer. Solo propone lo que está acá y activo. Lo marcado en rojo hay que revisarlo.
      </div>
      {cat.map((e) => (
        <div key={e.id} className="rounded-lg p-4 mb-3" style={{ background: "#111", border: `1px solid ${e.desactualizado ? "#D03B3B" : "#1F1F1F"}` }} data-testid={`catalogo-${e.letra || e.id}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="font-serif" style={{ fontSize: 16, color: "#F5F0E8" }}>{e.letra ? `[${e.letra}] ` : ""}{e.nombre}</div>
            <div className="flex items-center gap-2">
              {e.desactualizado && <span className="font-sans" style={{ fontSize: 11, color: "#D03B3B" }} data-testid={`catalogo-flag-${e.letra || e.id}`}>{e.pendiente_de_verificar ? "PENDIENTE DE VERIFICAR" : "+60 DÍAS SIN VERIFICAR"}</span>}
              <span className="font-sans" style={{ fontSize: 11, color: e.activa ? "#0CA30C" : "#6B6B6B" }}>{e.activa ? "activa" : "apagada"}</span>
            </div>
          </div>
          <div className="font-sans mb-1" style={{ fontSize: 12, color: "#9BC49B" }}><b>Resuelve bien:</b> {(e.resuelve_bien || []).join(" · ")}</div>
          <div className="font-sans mb-2" style={{ fontSize: 12, color: "#E0A0A0" }}><b>No puede:</b> {(e.no_puede || []).join(" · ")}</div>
          <div className="font-sans mb-3" style={{ fontSize: 11, color: "#8A8A8A" }}>
            corre_sola: {String(e.corre_sola)} · paralelo_max: {String(e.paralelo_max)} · navegador: {String(e.toca_navegador)} · perfiles_max: {String(e.perfiles_navegador_max)} · programas: {String(e.toca_programas_instalados)} · plan: {e.plan_requerido} · verificado: {e.verificado_el || "—"}
          </div>
          <div className="flex gap-2">
            <button onClick={() => verificar(e.id)} className="font-sans px-3 py-1.5 rounded-md" style={{ fontSize: 12, background: "#C9A84C", color: "#0A0A0A", fontWeight: 500 }} data-testid={`catalogo-verificar-${e.letra || e.id}`}>Marcar verificada hoy</button>
            <button onClick={() => toggleActiva(e)} className="font-sans px-3 py-1.5 rounded-md" style={{ fontSize: 12, background: "#161616", border: "1px solid #2A2A2A", color: "#D8D8D8" }} data-testid={`catalogo-toggle-${e.letra || e.id}`}>{e.activa ? "Apagar" : "Activar"}</button>
          </div>
        </div>
      ))}
    </div>
  );
}


function VistaConectables() {
  const [lista, setLista] = React.useState(null);
  const [pend, setPend] = React.useState([]);
  const [metodos, setMetodos] = React.useState(["claude", "chatgpt", "plataforma"]);
  const [editando, setEditando] = React.useState(null); // objeto en edición o null
  const vacio = { nombre: "", proveedor: "", metodo: "claude", plataforma_nombre: "", acciones: "", pasos: "", plan_requerido: "cualquiera", claves: "", limites: "", activo: true };

  const cargar = async () => {
    try {
      const { data } = await api.get("/admin/conectables");
      setLista(data.conectables || []); setMetodos(data.metodos || metodos);
    } catch { setLista([]); }
    try { const { data } = await api.get("/admin/conectables-pendientes"); setPend(data.pendientes || []); } catch {}
  };
  React.useEffect(() => { cargar(); }, []);

  const abrirNuevo = () => setEditando({ ...vacio });
  const abrirEditar = (e) => setEditando({
    ...e,
    acciones: (e.acciones || []).join(", "),
    pasos: (e.pasos || []).join("\n"),
    claves: (e.claves || []).join(", "),
  });
  const guardar = async () => {
    const f = editando;
    if (!f.nombre.trim()) { toast.error("Ponele un nombre."); return; }
    const body = {
      nombre: f.nombre.trim(), proveedor: f.proveedor.trim(), metodo: f.metodo,
      plataforma_nombre: f.metodo === "plataforma" ? (f.plataforma_nombre || "").trim() : "",
      acciones: f.acciones.split(",").map((s) => s.trim()).filter(Boolean),
      pasos: f.pasos.split("\n").map((s) => s.trim()).filter(Boolean),
      claves: f.claves.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean),
      plan_requerido: f.plan_requerido.trim() || "cualquiera", limites: (f.limites || "").trim(), activo: !!f.activo,
    };
    try {
      if (f.id) await api.patch(`/admin/conectables/${f.id}`, body);
      else await api.post("/admin/conectables", body);
      toast.success("Guardado."); setEditando(null); cargar();
    } catch (err) { toast.error(errText(err)); }
  };
  const toggle = async (e) => { try { await api.patch(`/admin/conectables/${e.id}`, { activo: !e.activo }); cargar(); } catch { toast.error("No pude actualizar."); } };
  const borrar = async (e) => { if (!window.confirm(`¿Borrar ${e.nombre}?`)) return; try { await api.delete(`/admin/conectables/${e.id}`); cargar(); } catch { toast.error("No pude borrar."); } };
  const resolver = async (p) => { try { await api.post(`/admin/conectables-pendientes/${p.id}/resolver`); cargar(); } catch { toast.error("No pude resolver."); } };

  const inp = { background: "#0D0D0D", border: "1px solid #2A2A2A", borderRadius: 8, color: "#F5F0E8", fontSize: 13, padding: "8px 10px", width: "100%" };
  const lbl = { fontSize: 11, color: "#8A8A8A", marginBottom: 4, display: "block", textTransform: "uppercase", letterSpacing: 0.5 };
  const metodoTxt = { claude: "Conector nativo de Claude", chatgpt: "Conector nativo de ChatGPT", plataforma: "Plataforma de conectores" };

  if (lista === null) return <div className="font-sans py-16 text-center" style={{ color: "#6B6B6B" }}>Cargando…</div>;

  return (
    <div data-testid="vista-conectables">
      <div className="flex items-center justify-between mb-4">
        <div className="font-sans" style={{ fontSize: 13, color: "#8A8A8A", maxWidth: 640 }}>
          La lista real de lo que se puede conectar hoy. El asistente la consulta antes de derivar a Olimpo: solo afirma lo que está acá.
        </div>
        <button onClick={abrirNuevo} className="font-sans flex items-center gap-2 px-3 py-2 rounded-md" style={{ fontSize: 13, background: "#C9A84C", color: "#0A0A0A", fontWeight: 500 }} data-testid="conectable-nuevo-btn"><Plus size={15} /> Nueva herramienta</button>
      </div>

      {/* Pendientes anotados por el asistente */}
      {pend.length > 0 && (
        <div className="rounded-lg p-4 mb-5" style={{ background: "#1A1608", border: "1px solid #6B5A1F" }} data-testid="conectables-pendientes">
          <div className="font-sans mb-2" style={{ fontSize: 12, color: "#C9A84C", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Pendientes por confirmar ({pend.length})</div>
          <div className="font-sans mb-3" style={{ fontSize: 12, color: "#9A9A9A" }}>El asistente no supo si se conectan y las dejó anotadas. Confirmá y cargalas arriba.</div>
          {pend.map((p) => (
            <div key={p.id} className="flex items-center justify-between py-2" style={{ borderTop: "1px solid #2A2412" }} data-testid={`pendiente-${p.id}`}>
              <div>
                <span className="font-sans" style={{ fontSize: 14, color: "#F5F0E8" }}>{p.herramienta}</span>
                {p.para_que && <span className="font-sans" style={{ fontSize: 12, color: "#8A8A8A" }}> — {p.para_que}</span>}
              </div>
              <button onClick={() => resolver(p)} className="font-sans px-3 py-1 rounded-md" style={{ fontSize: 12, background: "#161616", border: "1px solid #2A2A2A", color: "#D8D8D8" }} data-testid={`pendiente-resolver-${p.id}`}>Marcar resuelto</button>
            </div>
          ))}
        </div>
      )}

      {/* Formulario de alta/edición */}
      {editando && (
        <div className="rounded-lg p-4 mb-5" style={{ background: "#0F0F0F", border: "1px solid #C9A84C" }} data-testid="conectable-form">
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div><label style={lbl}>Nombre</label><input style={inp} value={editando.nombre} onChange={(e) => setEditando({ ...editando, nombre: e.target.value })} data-testid="conectable-nombre" /></div>
            <div><label style={lbl}>Proveedor</label><input style={inp} value={editando.proveedor} onChange={(e) => setEditando({ ...editando, proveedor: e.target.value })} data-testid="conectable-proveedor" /></div>
            <div><label style={lbl}>Cómo se conecta</label>
              <select style={inp} value={editando.metodo} onChange={(e) => setEditando({ ...editando, metodo: e.target.value })} data-testid="conectable-metodo">
                {metodos.map((m) => <option key={m} value={m}>{metodoTxt[m] || m}</option>)}
              </select>
            </div>
            {editando.metodo === "plataforma" && (
              <div><label style={lbl}>Nombre de la plataforma</label><input style={inp} placeholder="Ej: Composio" value={editando.plataforma_nombre} onChange={(e) => setEditando({ ...editando, plataforma_nombre: e.target.value })} data-testid="conectable-plataforma" /></div>
            )}
            <div><label style={lbl}>Qué se puede hacer (coma)</label><input style={inp} placeholder="leer, crear, actualizar" value={editando.acciones} onChange={(e) => setEditando({ ...editando, acciones: e.target.value })} data-testid="conectable-acciones" /></div>
            <div><label style={lbl}>Plan que hace falta</label><input style={inp} value={editando.plan_requerido} onChange={(e) => setEditando({ ...editando, plan_requerido: e.target.value })} data-testid="conectable-plan" /></div>
            <div><label style={lbl}>Palabras clave para detectarla (coma)</label><input style={inp} placeholder="stripe, factura, invoice" value={editando.claves} onChange={(e) => setEditando({ ...editando, claves: e.target.value })} data-testid="conectable-claves" /></div>
            <div><label style={lbl}>Límite (opcional)</label><input style={inp} value={editando.limites} onChange={(e) => setEditando({ ...editando, limites: e.target.value })} data-testid="conectable-limites" /></div>
          </div>
          <div className="mb-3"><label style={lbl}>Paso a paso para conectarla (uno por línea, por nombres)</label>
            <textarea style={{ ...inp, minHeight: 110 }} value={editando.pasos} onChange={(e) => setEditando({ ...editando, pasos: e.target.value })} data-testid="conectable-pasos" />
          </div>
          <div className="flex gap-2">
            <button onClick={guardar} className="font-sans px-4 py-2 rounded-md" style={{ fontSize: 13, background: "#C9A84C", color: "#0A0A0A", fontWeight: 500 }} data-testid="conectable-guardar">Guardar</button>
            <button onClick={() => setEditando(null)} className="font-sans px-4 py-2 rounded-md" style={{ fontSize: 13, background: "#161616", border: "1px solid #2A2A2A", color: "#D8D8D8" }} data-testid="conectable-cancelar">Cancelar</button>
          </div>
        </div>
      )}

      {/* Lista */}
      {lista.length === 0 && !editando && <div className="font-sans py-8 text-center" style={{ color: "#6B6B6B", fontSize: 13 }}>Todavía no cargaste ninguna herramienta conectable.</div>}
      {lista.map((e) => (
        <div key={e.id} className="rounded-lg p-4 mb-3" style={{ background: "#111", border: "1px solid #1F1F1F" }} data-testid={`conectable-${e.id}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="font-serif" style={{ fontSize: 16, color: "#F5F0E8" }}>{e.nombre} <span className="font-sans" style={{ fontSize: 11, color: "#8A8A8A" }}>· {metodoTxt[e.metodo] || e.metodo}{e.metodo === "plataforma" && e.plataforma_nombre ? ` (${e.plataforma_nombre})` : ""}</span></div>
            <span className="font-sans" style={{ fontSize: 11, color: e.activo ? "#0CA30C" : "#6B6B6B" }}>{e.activo ? "activo" : "apagado"}</span>
          </div>
          <div className="font-sans mb-1" style={{ fontSize: 12, color: "#9BC49B" }}><b>Se puede:</b> {(e.acciones || []).join(", ") || "—"} · <b>Plan:</b> {e.plan_requerido || "cualquiera"}</div>
          {e.limites && <div className="font-sans mb-1" style={{ fontSize: 12, color: "#E0A0A0" }}><b>Límite:</b> {e.limites}</div>}
          <div className="font-sans mb-2" style={{ fontSize: 11, color: "#7A7A7A" }}><b>Detecta con:</b> {(e.claves || []).join(", ") || "—"}</div>
          {(e.pasos || []).length > 0 && (
            <ol className="font-sans mb-3" style={{ fontSize: 12, color: "#B8B8B8", paddingLeft: 18, listStyle: "decimal" }}>
              {(e.pasos || []).map((p, i) => <li key={i} style={{ marginBottom: 2 }}>{p}</li>)}
            </ol>
          )}
          <div className="flex gap-2">
            <button onClick={() => abrirEditar(e)} className="font-sans px-3 py-1.5 rounded-md" style={{ fontSize: 12, background: "#161616", border: "1px solid #2A2A2A", color: "#D8D8D8" }} data-testid={`conectable-editar-${e.id}`}>Editar</button>
            <button onClick={() => toggle(e)} className="font-sans px-3 py-1.5 rounded-md" style={{ fontSize: 12, background: "#161616", border: "1px solid #2A2A2A", color: "#D8D8D8" }} data-testid={`conectable-toggle-${e.id}`}>{e.activo ? "Apagar" : "Activar"}</button>
            <button onClick={() => borrar(e)} className="font-sans px-3 py-1.5 rounded-md" style={{ fontSize: 12, background: "#161616", border: "1px solid #3A2020", color: "#E0A0A0" }} data-testid={`conectable-borrar-${e.id}`}>Borrar</button>
          </div>
        </div>
      ))}
      <EscaleraPrecios />
    </div>
  );
}


function EscaleraPrecios() {
  const [cfg, setCfg] = React.useState(null);
  const [guardando, setGuardando] = React.useState(false);
  const cargar = async () => {
    try { const { data } = await api.get("/admin/escalera"); setCfg(data.escalera || {}); }
    catch { setCfg({}); }
  };
  React.useEffect(() => { cargar(); }, []);
  const guardar = async () => {
    setGuardando(true);
    try {
      const comp = cfg.composio || {};
      const wass = cfg.wassenger || {};
      const body = {
        composio: {
          url: (comp.url || "").trim(),
          linea_honesta: (comp.linea_honesta || "").trim(),
          pasos: (comp.pasosText != null ? comp.pasosText : (comp.pasos || []).join("\n")).split("\n").map((s) => s.trim()).filter(Boolean),
        },
        wassenger: {
          url: (wass.url || "").trim(),
          prueba_dias: parseInt(wass.prueba_dias, 10) || 0,
          precio_mensual: (wass.precio_mensual || "").trim(),
          nota_meta: (wass.nota_meta || "").trim(),
          aviso_corte: (wass.aviso_corte || "").trim(),
          pasos: (wass.pasosText != null ? wass.pasosText : (wass.pasos || []).join("\n")).split("\n").map((s) => s.trim()).filter(Boolean),
        },
      };
      await api.patch("/admin/escalera", body);
      toast.success("Escalera actualizada. Los precios ya salen de acá."); cargar();
    } catch (err) { toast.error(errText(err) || "No pude guardar."); }
    finally { setGuardando(false); }
  };
  const inp = { background: "#0D0D0D", border: "1px solid #2A2A2A", borderRadius: 8, color: "#F5F0E8", fontSize: 13, padding: "8px 10px", width: "100%" };
  const lbl = { fontSize: 11, color: "#8A8A8A", marginBottom: 4, display: "block", textTransform: "uppercase", letterSpacing: 0.5 };
  if (cfg === null) return null;
  const comp = cfg.composio || {};
  const wass = cfg.wassenger || {};
  const setComp = (k, v) => setCfg({ ...cfg, composio: { ...comp, [k]: v } });
  const setWass = (k, v) => setCfg({ ...cfg, wassenger: { ...wass, [k]: v } });
  return (
    <div className="rounded-lg p-4 mt-8" style={{ background: "#0F0F0F", border: "1px solid #6B5A1F" }} data-testid="escalera-precios">
      <div className="font-serif mb-1" style={{ fontSize: 16, color: "#C9A84C" }}>La escalera · precios y tutoriales</div>
      <div className="font-sans mb-4" style={{ fontSize: 12, color: "#8A8A8A", maxWidth: 640 }}>
        Los precios y los pasos de Composio y Wassenger salen de acá, no del código. Cambialos cuando cambien y el asistente los usa al toque.
      </div>

      <div className="font-sans mb-2" style={{ fontSize: 13, color: "#F5F0E8", fontWeight: 600 }}>Composio (escalón 2)</div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div><label style={lbl}>URL</label><input style={inp} value={comp.url || ""} onChange={(e) => setComp("url", e.target.value)} data-testid="escalera-composio-url" /></div>
      </div>
      <div className="mb-3"><label style={lbl}>Línea honesta (antes del tutorial)</label>
        <textarea style={{ ...inp, minHeight: 60 }} value={comp.linea_honesta || ""} onChange={(e) => setComp("linea_honesta", e.target.value)} data-testid="escalera-composio-linea" />
      </div>
      <div className="mb-4"><label style={lbl}>Pasos del tutorial (uno por línea)</label>
        <textarea style={{ ...inp, minHeight: 130 }} value={comp.pasosText != null ? comp.pasosText : (comp.pasos || []).join("\n")} onChange={(e) => setComp("pasosText", e.target.value)} data-testid="escalera-composio-pasos" />
      </div>

      <div className="font-sans mb-2" style={{ fontSize: 13, color: "#F5F0E8", fontWeight: 600 }}>Wassenger (escalón 4 · solo WhatsApp)</div>
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div><label style={lbl}>URL</label><input style={inp} value={wass.url || ""} onChange={(e) => setWass("url", e.target.value)} data-testid="escalera-wassenger-url" /></div>
        <div><label style={lbl}>Días de prueba</label><input style={inp} type="number" value={wass.prueba_dias ?? ""} onChange={(e) => setWass("prueba_dias", e.target.value)} data-testid="escalera-wassenger-prueba" /></div>
        <div><label style={lbl}>Precio mensual</label><input style={inp} placeholder="USD 39,90" value={wass.precio_mensual || ""} onChange={(e) => setWass("precio_mensual", e.target.value)} data-testid="escalera-wassenger-precio" /></div>
      </div>
      <div className="mb-3"><label style={lbl}>Nota sobre lo que cobra Meta aparte</label>
        <textarea style={{ ...inp, minHeight: 55 }} value={wass.nota_meta || ""} onChange={(e) => setWass("nota_meta", e.target.value)} data-testid="escalera-wassenger-meta" />
      </div>
      <div className="mb-3"><label style={lbl}>Aviso de corte al terminar la prueba</label>
        <textarea style={{ ...inp, minHeight: 55 }} value={wass.aviso_corte || ""} onChange={(e) => setWass("aviso_corte", e.target.value)} data-testid="escalera-wassenger-aviso" />
      </div>
      <div className="mb-4"><label style={lbl}>Pasos del tutorial (uno por línea)</label>
        <textarea style={{ ...inp, minHeight: 130 }} value={wass.pasosText != null ? wass.pasosText : (wass.pasos || []).join("\n")} onChange={(e) => setWass("pasosText", e.target.value)} data-testid="escalera-wassenger-pasos" />
      </div>

      <button onClick={guardar} disabled={guardando} className="font-sans px-4 py-2 rounded-md" style={{ fontSize: 13, background: "#C9A84C", color: "#0A0A0A", fontWeight: 500 }} data-testid="escalera-guardar">{guardando ? "Guardando…" : "Guardar escalera"}</button>
    </div>
  );
}


function VistaEncuestas() {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    (async () => {
      try { const r = await api.get("/admin/encuestas"); setData(r.data); }
      catch { setData({ total: 0, ranking_traba: [], comentarios: [] }); }
    })();
  }, []);
  if (!data) return <Cargando />;
  const maxTraba = Math.max(1, ...(data.ranking_traba || []).map((r) => r.cantidad));
  const Card = ({ children }) => <div className="rounded-xl p-5" style={{ border: "1px solid #1A1A1A", background: "#0F0F0F" }}>{children}</div>;
  const label = { solo: "Solo", grupo: "Preguntó en el grupo", no_termine: "No lo terminó", si: "Sí", no: "No", no_se: "Todavía no sé" };
  return (
    <div className="space-y-6" data-testid="vista-encuestas">
      {data.total === 0 && <p className="text-gris text-sm" data-testid="encuestas-vacias">Todavía no hay encuestas respondidas. Aparecen cuando alguien termina de poner un proceso a andar.</p>}
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <div className="font-sans mb-1" style={{ fontSize: 12, color: "#8A8A8A", letterSpacing: "0.1em" }}>DIFICULTAD PROMEDIO</div>
          <div className="titulo text-dorado" style={{ fontSize: 44 }} data-testid="enc-promedio">{data.promedio_dificultad != null ? data.promedio_dificultad : "—"}<span style={{ fontSize: 20, color: "#8A8A8A" }}> / 5</span></div>
        </Card>
        <Card>
          <div className="font-sans mb-1" style={{ fontSize: 12, color: "#8A8A8A", letterSpacing: "0.1em" }}>ENCUESTAS</div>
          <div className="titulo text-crema" style={{ fontSize: 44 }} data-testid="enc-total">{data.total}</div>
        </Card>
        <Card>
          <div className="font-sans mb-2" style={{ fontSize: 12, color: "#8A8A8A", letterSpacing: "0.1em" }}>¿LO PUDO HACER SOLO?</div>
          {Object.entries(data.pudo_solo || {}).map(([k, v]) => (
            <div key={k} className="flex justify-between text-sm" style={{ color: "#C9C4BC" }}><span>{label[k] || k}</span><span className="text-dorado">{v}</span></div>
          ))}
          {Object.keys(data.pudo_solo || {}).length === 0 && <span className="text-gris text-sm">—</span>}
        </Card>
      </div>

      <Card>
        <div className="font-sans mb-4" style={{ fontSize: 12, color: "#8A8A8A", letterSpacing: "0.1em" }}>DÓNDE SE TRABA LA GENTE (ranking)</div>
        <div className="space-y-2.5" data-testid="enc-ranking">
          {(data.ranking_traba || []).length === 0 && <span className="text-gris text-sm">—</span>}
          {(data.ranking_traba || []).map((r) => (
            <div key={r.clave} data-testid={`enc-traba-${r.clave}`}>
              <div className="flex justify-between text-sm mb-1" style={{ color: "#C9C4BC" }}><span>{r.label}</span><span className="text-dorado">{r.cantidad}</span></div>
              <div className="h-2 rounded-full" style={{ background: "#1A1A1A" }}>
                <div className="h-2 rounded-full" style={{ width: `${(r.cantidad / maxTraba) * 100}%`, background: "#C9A84C" }} />
              </div>
            </div>
          ))}
        </div>
      </Card>

      {(data.comentarios || []).length > 0 && (
        <Card>
          <div className="font-sans mb-3" style={{ fontSize: 12, color: "#8A8A8A", letterSpacing: "0.1em" }}>QUÉ LOS HABRÍA AYUDADO</div>
          <div className="space-y-2">
            {data.comentarios.map((c, i) => (
              <p key={i} className="text-sm" style={{ color: "#C9C4BC" }}>· {c.texto}</p>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
